import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { hydrate, switchUser } from 'core/actions/product/state';
import { homeRequest } from 'core/services/product';
import { restartToken } from './session';
import { CourseNodeType } from 'types/domain/course';

export interface IQueueItem {
  courseNodeId: number;
  courseProgressId: number;
  moduleId?: number;
  sessionAttemptId?: number;
  courseNodeType?: CourseNodeType;
  feedback?: boolean;
}

export interface IQueue {
  progressItems: Record<number, IQueueItem[]>;
}

export const queueSlice = createSlice({
  name: 'queue',
  initialState: { progressItems: {} } as IQueue,
  reducers: {
    queueItem: (state, action: PayloadAction<IQueueItem>) => {
      if (!state.progressItems[action.payload.courseProgressId])
        state.progressItems[action.payload.courseProgressId] = [];

      //Don't add duplicates
      if (
        action.payload.courseNodeType === CourseNodeType.MENU ||
        !state.progressItems[action.payload.courseProgressId].some(
          item =>
            item.courseNodeId === action.payload.courseNodeId &&
            item.sessionAttemptId === action.payload.sessionAttemptId,
        )
      )
        state.progressItems[action.payload.courseProgressId].push(action.payload);
    },
    clearQueue: state => {
      state.progressItems = {};
    },
    resetQueue: (state, action: PayloadAction<IQueueItem>) => {
      const index = state.progressItems[action.payload.courseProgressId].findIndex(
        item => item.courseNodeId === action.payload.courseNodeId,
      );

      if (index > -1)
        state.progressItems[action.payload.courseProgressId] = state.progressItems[
          action.payload.courseProgressId
        ].slice(0, index + 1);
    },
  },
  extraReducers: builder => {
    builder.addCase(hydrate, (state, action) => {
      if (action.payload.queue) state = action.payload.queue;
    });
    builder.addCase(homeRequest.pending, (state, action) => {
      state.progressItems[action.meta.arg.courseProgressId] = [];
    });
    builder.addCase(restartToken, (state, action) => {
      state.progressItems[action.payload.courseProgressId] = [];
    });
    // Someone else is signed in on this device now. The queue is keyed by course
    // progress, which is per user, so none of it applies to them — and keeping it
    // is what put nodes the new user was never assigned (payment, verification)
    // back on screen.
    builder.addCase(switchUser, state => {
      state.progressItems = {};
    });
  },
});

export const { queueItem, resetQueue } = queueSlice.actions;

export default queueSlice.reducer;
